'use client';

import { useCallback, useEffect, useState } from 'react';
import { Minus, Sparkles, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { BALLEE_USER_ID } from '@kit/chat/lib';
import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { Avatar, AvatarFallback, AvatarImage } from '@kit/ui/avatar';
import { Badge } from '@kit/ui/badge';
import { Button } from '@kit/ui/button';
import { useIsMobile } from '@kit/ui/hooks/use-mobile';
import { Spinner } from '@kit/ui/spinner';
import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

import { useFloatingChat } from './floating-chat-context';
import { FloatingChatView } from './floating-chat-view';

export function FloatingChatPanel() {
  const { t } = useTranslation();
  const {
    isOpen,
    setIsOpen,
    selectedConversationId,
    setSelectedConversationId,
    overrideConversationId,
    overrideTitle,
    overrideSubtitle,
    overrideAvatarUrl,
    clearOverride,
    dimensions,
  } = useFloatingChat();
  const isMobile = useIsMobile();
  const client = useSupabase();
  const [isCreating, setIsCreating] = useState(false);

  // The panel shows an item-anchored override conversation (e.g. marketplace
  // "Message seller") when set, otherwise the persisted Ballee AI conversation.
  const effectiveConversationId =
    overrideConversationId ?? selectedConversationId;

  // Closing always clears the override so the floating button reopens the AI
  // assistant next time, not the last seller DM.
  const handleClose = useCallback(() => {
    clearOverride();
    setIsOpen(false);
  }, [clearOverride, setIsOpen]);

  // Minimizing keeps the override, so reopening the bubble returns to the
  // same conversation (e.g. the seller DM the buyer was mid-thread in).
  const handleMinimize = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  // Auto-create/get Ballee conversation when the panel opens with NO
  // conversation at all (neither an override nor a persisted AI one).
  const ensureBalleeConversation = useCallback(async () => {
    if (selectedConversationId || overrideConversationId || isCreating) return;

    setIsCreating(true);
    try {
      const { data, error } = await client.rpc(
        'get_or_create_direct_conversation',
        { p_other_user_id: BALLEE_USER_ID },
      );

      if (!error && data) {
        setSelectedConversationId(data as string);
      }
    } catch {
      // Ignore errors — will show error state in chat view
    } finally {
      setIsCreating(false);
    }
  }, [
    selectedConversationId,
    overrideConversationId,
    isCreating,
    client,
    setSelectedConversationId,
  ]);

  useEffect(() => {
    if (isOpen && !effectiveConversationId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- async fetch on panel open; setState inside ensureBalleeConversation is the effect's intent
      void ensureBalleeConversation();
    }
  }, [isOpen, effectiveConversationId, ensureBalleeConversation]);

  // The launcher now lives in the header/mobile nav (FlowAiButton), so the
  // panel renders nothing until opened. On mobile it fills the screen; on
  // desktop it's the anchored bottom-right card.
  if (!isOpen) {
    return null;
  }

  return (
    <div
      className={cn(
        'bg-background z-50 flex flex-col overflow-hidden border shadow-lg',
        'transition-all duration-300 ease-in-out',
        'motion-reduce:transition-none',
        isMobile ? 'fixed inset-0' : 'fixed right-6 bottom-6 rounded-lg',
        isOpen
          ? 'pointer-events-auto scale-100 opacity-100'
          : 'pointer-events-none scale-95 opacity-0',
      )}
      style={
        isMobile
          ? undefined
          : {
              width: dimensions.width,
              height: dimensions.height,
            }
      }
      role="dialog"
      aria-label={
        overrideConversationId
          ? (overrideTitle ?? t('chat:floatingChat.defaultConversationTitle'))
          : t('chat:floatingChat.aiAssistantLabel')
      }
      aria-hidden={!isOpen}
    >
      {/* Header — AI badge for the assistant; avatar + title + listing
          context for an item-anchored override (e.g. a marketplace seller
          DM). */}
      <div className="bg-muted/50 flex items-center justify-between border-b px-3 py-2">
        <div className="flex min-w-0 items-center gap-2">
          {overrideConversationId ? (
            <>
              <Avatar className="h-7 w-7 flex-shrink-0">
                <AvatarImage
                  src={overrideAvatarUrl ?? undefined}
                  alt={
                    overrideTitle ??
                    t('chat:floatingChat.defaultConversationTitle')
                  }
                />
                <AvatarFallback className="text-xs">
                  {/* i18n-ignore avatar initial fallback letter, not user-facing prose */}
                  {(overrideTitle ?? 'C')
                    .split(' ')
                    .slice(0, 2)
                    .map((part) => part.slice(0, 1).toUpperCase())
                    .join('')}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <h2 className="truncate text-sm font-semibold">
                  {overrideTitle ?? t('chat:floatingChat.defaultMessageTitle')}
                </h2>
                {overrideSubtitle && (
                  <p className="text-muted-foreground truncate text-xs">
                    {overrideSubtitle}
                  </p>
                )}
              </div>
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4 text-purple-500" />
              <h2 className="text-sm font-semibold">
                <Trans i18nKey="chat:floatingChat.title" />
              </h2>
              <Badge
                variant="secondary"
                className="bg-purple-100 px-1.5 py-0 text-[10px] text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
              >
                <Trans i18nKey="chat:inbox.badge.ai" />
              </Badge>
            </>
          )}
        </div>
        <div className="flex flex-shrink-0 items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={handleMinimize}
            aria-label={t('chat:floatingChat.minimizeLabel')}
          >
            <Minus className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={handleClose}
            aria-label={t('chat:floatingChat.closeLabel')}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {isCreating ? (
          <div className="flex flex-1 items-center justify-center">
            <div className="flex flex-col items-center gap-2">
              <Spinner className="h-6 w-6" />
              <p className="text-muted-foreground text-sm">
                <Trans i18nKey="chat:floatingChat.startingConversation" />
              </p>
            </div>
          </div>
        ) : effectiveConversationId ? (
          <FloatingChatView
            conversationId={effectiveConversationId}
            onBack={handleClose}
            showBookingInquiry={!overrideConversationId}
          />
        ) : (
          <div className="flex flex-1 items-center justify-center p-4 text-center">
            <p className="text-muted-foreground text-sm">
              <Trans i18nKey="chat:floatingChat.unableToStart" />
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
